"use client";

import { useEffect, useState } from "react";
import GroupsDirectoryClient from "@/components/groups/GroupsDirectoryClient";
import { fetchWithAuth } from "@/lib/auth";
import { TourButton } from "@/components/tours/TourButton";
import { useAutoTour } from "@/components/tours/useAutoTour";
import { TOURS } from "@/components/tours/catalog";

interface GroupEntry {
    id: number;
    name: string;
    type: string;
    status: string;
    region_code: string;
    description: string;
    url: string;
    volunteer_count?: number;
    active_count?: number;
    inactive_count?: number;
    has_assignments?: boolean;
}

type MapMode = "assignments_only" | "all_active";

export function OrgGroupsPanel() {
    const [groups, setGroups] = useState<GroupEntry[]>([]);
    const [mapDisplayMode, setMapDisplayMode] = useState<MapMode>("assignments_only");
    const [loading, setLoading] = useState(true);
    const { replay: replayTour } = useAutoTour("member.orgGroups", TOURS["member.orgGroups"]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [groupsRes, modeRes] = await Promise.all([
                    fetchWithAuth("/api/v1/organizations/all-groups", { cache: "no-store" }),
                    fetchWithAuth("/api/v1/settings/map_display_mode", { cache: "no-store" }),
                ]);
                const groupsData: GroupEntry[] = groupsRes.ok ? await groupsRes.json() : [];
                let mode: MapMode = "assignments_only";
                if (modeRes.ok) {
                    const data = await modeRes.json();
                    mode = data.value === "all_active" ? "all_active" : "assignments_only";
                }
                if (!cancelled) {
                    setGroups(groupsData);
                    setMapDisplayMode(mode);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const stats = {
        committees: groups.filter((g) => g.type === "committee").length,
        chapters: groups.filter((g) => g.type === "chapter").length,
        communities: groups.filter((g) => g.type === "community").length,
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-40">
                <div className="w-10 h-10 border-4 border-[#e2e8f0] border-t-[#003d45] animate-spin rounded-full" />
            </div>
        );
    }

    return (
        <>
            <div className="flex justify-end px-6 pt-4">
                <TourButton onClick={replayTour} />
            </div>
            <GroupsDirectoryClient groups={groups} stats={stats} mapDisplayMode={mapDisplayMode} />
        </>
    );
}
